import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Gift } from 'lucide-react'

import { Alert } from '../ui/Alert'
import { Button } from '../ui/Button'
import type { GatingStatus } from '../../services/gating'

/**
 * BANDEAU D'ESSAI GRATUIT (LOT-GATING).
 *
 * Vit dans la `NotificationRegion` de l'AppShell (zone H9) — persistant, non
 * dismissible : tant que le tenant est en essai, l'agent doit savoir où il en
 * est AVANT de tenter un appel (sur refus de token, la scène téléphonie gelée
 * n'affiche qu'une erreur générique — assumé, poli post-bascule).
 *
 * Trois états :
 *   - tenant illimité (exempt/abonné) → RIEN (le parent ne doit alors même pas
 *     rendre l'élément, pour que la zone vide n'existe pas — garde H9 ; le
 *     `null` d'ici est une seconde ceinture) ;
 *   - essai sous quota → ton `info`, compteurs restants + CTA /upgrade ;
 *   - au moins un quota épuisé → ton `warning` + CTA.
 *
 * Aucune arithmétique métier côté client : les compteurs viennent du serveur
 * (GET /api/gating/status), on ne fait que soustraire pour AFFICHER un reste.
 */
export interface TrialBannerProps {
  status?: GatingStatus
}

export function TrialBanner({ status }: TrialBannerProps) {
  const { t } = useTranslation('shell')
  const navigate = useNavigate()

  if (!status?.trial) return null

  const callsLeft = Math.max(0, (status.calls.max ?? 0) - status.calls.used)
  const emailsLeft = Math.max(0, (status.emails.max ?? 0) - status.emails.used)
  const callsExhausted = callsLeft === 0
  const emailsExhausted = emailsLeft === 0
  const exhausted = callsExhausted || emailsExhausted

  const description = exhausted
    ? callsExhausted && emailsExhausted
      ? t('gating.exhaustedBoth')
      : callsExhausted
        ? t('gating.exhaustedCalls', { emailsLeft })
        : t('gating.exhaustedEmails', { callsLeft })
    : t('gating.remaining', { callsLeft, emailsLeft })

  return (
    <Alert
      tone={exhausted ? 'warning' : 'info'}
      icon={Gift}
      // G-4 : crédits d'APPEL épuisés → le titre nomme la chose (« Crédits
      // d'appel épuisés ») en ton warning calme — une offre à saisir, pas une
      // panne. E-mails seuls épuisés → titre générique inchangé.
      title={t(
        callsExhausted
          ? 'gating.exhaustedCallsTitle'
          : exhausted
            ? 'gating.exhaustedTitle'
            : 'gating.title',
      )}
      action={
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/upgrade')}
        >
          {t('gating.upgradeCta')}
        </Button>
      }
    >
      {description}
    </Alert>
  )
}

export default TrialBanner
